import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { LoginCredentials, User } from '../types'
import { apiService } from '../services/api'
import { AUTH_LOGOUT_EVENT, getTimeUntilExpirationMs, loadUserFromStorage, logoutEverywhere } from '../utils/tokenUtils'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return ctx
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setUser(loadUserFromStorage())
    setIsLoading(false)
  }, [])

  useEffect(() => {
    const onLogout = () => setUser(null)
    window.addEventListener(AUTH_LOGOUT_EVENT, onLogout as EventListener)
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, onLogout as EventListener)
  }, [])

  useEffect(() => {
    if (!user) return

    const accessToken = localStorage.getItem('accessToken')
    if (!accessToken) return

    const timeUntilExpirationMs = getTimeUntilExpirationMs(accessToken)
    if (timeUntilExpirationMs === null) return

    if (timeUntilExpirationMs <= 0) {
      logoutEverywhere()
      setUser(null)
      return
    }

    const timeoutId = window.setTimeout(() => {
      logoutEverywhere()
      setUser(null)
    }, timeUntilExpirationMs)

    return () => window.clearTimeout(timeoutId)
  }, [user])

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true)
    try {
      await apiService.login(credentials)
      setUser(loadUserFromStorage())
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    logoutEverywhere()
    setUser(null)
  }

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout
    }),
    [user, isLoading]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
